<template>
  <!-- Accounting (Workspace369 lib/view/accounting/): the setup wizard (setup/accounting_setup_wizard.dart),
       the hub (accounting_hub_screen.dart, widgets/accounting_hub_parts.dart), the chart of accounts
       (chart_of_accounts_screen.dart, seeded from the "General Business" template in
       services/accounting/chart_of_accounts_templates.dart), the report list
       (reports/accounting_reports_screen.dart) and Profit & Loss (reports/profit_loss_report_screen.dart).
       Strings are the app's (app_en.arb setupWizard*, accounting hub KPI labels, report* keys; template
       names, account groups and report descriptions are hardcoded in the app). Entry numbers follow
       the API's JE-YYYY-NNNN; memos follow functions-accounting postingRules.ts. Only the neutral
       templates are shown. Amounts and names are sample data. States:
         template  wizard step 1, Choose a Template
         review    wizard step 4, Review & Confirm
         hub       the hub: KPI strip, recent journal entries, Go to
         coa       the chart of accounts
         reports   Financial Reports: the Specialist reports, the rest under Commander
         pnl       Profit & Loss, compared with the previous period -->
  <StoryAppWindow :title="title" :icon="icons.book">
    <!-- Setup wizard -->
    <section class="saw-layer acs-wiz" :class="{ 'is-on': state === 'template' || state === 'review' }">
      <ol class="acs-steps">
        <li v-for="(s, i) in wizardSteps" :key="s" :class="{ 'is-done': state === 'review' && i < 3, 'is-on': (state === 'template' && i === 0) || (state === 'review' && i === 3) }">
          <i>{{ state === "review" && i < 3 ? "✓" : i + 1 }}</i><span>{{ s }}</span>
        </li>
      </ol>

      <div class="acs-step" :class="{ 'is-on': state === 'template' }">
        <b class="acs-h">Choose a Template</b>
        <p class="acs-sub">Select an industry template to pre-populate your chart of accounts, or start from scratch.</p>
        <div class="saw-card acs-tpl is-picked">
          <i class="acs-radio" />
          <span><b>General Business</b><small>A well-rounded chart of accounts suitable for most businesses.</small></span>
        </div>
        <div class="saw-card acs-tpl">
          <i class="acs-radio" />
          <span><b>Blank (Start from Scratch)</b><small>Only the essential system accounts. Build your own chart of accounts from the ground up.</small></span>
        </div>
      </div>

      <div class="acs-step" :class="{ 'is-on': state === 'review' }">
        <b class="acs-h">Review &amp; Confirm</b>
        <p class="acs-sub">Review your selections before setting up accounting.</p>
        <dl class="saw-card acs-review">
          <div><dt>Template</dt><dd>General Business</dd></div>
          <div><dt>Fiscal Year</dt><dd>January 2026 - December 2026</dd></div>
          <div><dt>Accounting Method</dt><dd>Accrual Basis</dd></div>
        </dl>
      </div>

      <div class="acs-bar">
        <span class="saw-btn-quiet">Back</span>
        <span class="saw-btn">{{ state === "review" ? "Set Up Accounting" : "Next" }}</span>
      </div>
    </section>

    <!-- Hub -->
    <section class="saw-layer acs-hub" :class="{ 'is-on': state === 'hub' }">
      <dl class="acs-kpis">
        <div v-for="k in kpis" :key="k.label"><dt>{{ k.label }}</dt><dd class="saw-num">{{ k.value }}</dd><small v-if="k.note">{{ k.note }}</small></div>
      </dl>
      <div class="saw-card acs-je">
        <div class="acs-je-head"><b>Recent journal entries</b><span class="acs-link">View All</span></div>
        <div v-for="(e, i) in entries" :key="e.no" class="acs-je-row" :style="{ '--i': i }">
          <span class="acs-je-main"><b>{{ e.memo }}</b><small class="saw-num">{{ e.date }}<span class="acs-je-no"> · {{ e.no }}</span></small></span>
          <em class="saw-tag saw-tag--blue">Posted</em>
          <b class="saw-num acs-je-amt">{{ e.amount }}</b>
        </div>
      </div>
    </section>

    <!-- Chart of accounts -->
    <section class="saw-layer acs-coa" :class="{ 'is-on': state === 'coa' }">
      <span class="acs-search"><svg viewBox="0 0 24 24"><path :d="icons.search" /></svg>Search accounts...</span>
      <div class="saw-card acs-tree">
        <template v-for="g in chart" :key="g.group">
          <p class="acs-group"><b>{{ g.group }}</b></p>
          <div v-for="a in g.accounts" :key="a.code" class="acs-acc">
            <span class="saw-num acs-code">{{ a.code }}</span>
            <span class="acs-name">{{ a.name }}</span>
            <b class="saw-num">{{ a.balance }}</b>
          </div>
        </template>
      </div>
    </section>

    <!-- Report list -->
    <section class="saw-layer acs-reports" :class="{ 'is-on': state === 'reports' }">
      <span><b class="acs-h">Financial Reports</b><p class="acs-sub">Select a report to view your financial data.</p></span>
      <div class="acs-grid">
        <div v-for="r in openReports" :key="r.title" class="saw-card acs-rep"><b>{{ r.title }}</b><small>{{ r.copy }}</small></div>
      </div>
      <p class="acs-more">More reports on Commander</p>
      <div class="acs-grid acs-grid--dim">
        <div v-for="r in moreReports" :key="r.title" class="saw-card acs-rep"><b>{{ r.title }}</b><small>{{ r.copy }}</small></div>
      </div>
    </section>

    <!-- Profit & Loss -->
    <section class="saw-layer acs-pnl" :class="{ 'is-on': state === 'pnl' }">
      <div class="acs-pnl-head">
        <span><b class="acs-h">Profit &amp; Loss</b><small class="saw-muted">Oct 1, 2026 – Oct 31, 2026 &nbsp;|&nbsp; Accrual Basis</small></span>
        <span class="saw-btn-quiet">Export PDF</span>
      </div>
      <div class="saw-card acs-pl">
        <div class="acs-pl-row acs-pl-cols"><span /><span>Current</span><span>Previous</span><span>Change</span></div>
        <template v-for="sec in pnl" :key="sec.title">
          <div class="acs-pl-row acs-pl-sec"><b>{{ sec.title }}</b><b class="saw-num">{{ sec.total }}</b><span class="saw-num">{{ sec.prev }}</span><span class="saw-num" :class="sec.up ? 'is-up' : 'is-down'">{{ sec.change }}</span></div>
          <div v-for="l in sec.lines" :key="l.name" class="acs-pl-row acs-pl-line"><span>{{ l.name }}</span><span class="saw-num">{{ l.value }}</span><span class="saw-num">{{ l.prev }}</span><span class="saw-num" :class="l.change.startsWith('-') ? 'is-down' : 'is-up'">{{ l.change }}</span></div>
        </template>
        <div class="acs-pl-row acs-pl-net"><b>Net Income</b><b class="saw-num">$12,250.00</b><span class="saw-num">$9,420.00</span><span class="saw-num is-up">+$2,830.00</span></div>
      </div>
    </section>
  </StoryAppWindow>
</template>

<script setup>
const props = defineProps({
  state: { type: String, default: "hub" },
});

const title = computed(() => {
  if (props.state === "template" || props.state === "review") return "Accounting Setup";
  if (props.state === "coa") return "Chart of Accounts";
  return "Accounting";
});

const wizardSteps = ["Template", "Fiscal Year", "Method", "Review"];

const kpis = [
  { label: "Cash Position", value: "$24,310.00", note: "" },
  { label: "Accounts Receivable", value: "$12,100.00", note: "Tracks money owed to you by customers." },
  { label: "Accounts Payable", value: "$1,240.00", note: "Tracks money you owe to vendors." },
  { label: "Net Income", value: "$12,250.00", note: "Revenue $18,400.00 · Expenses $6,150.00" },
];

const entries = [
  { date: "Oct 21, 2026", no: "JE-2026-0142", memo: 'Auto-posted: Expense "Figma"', amount: "$45.00" },
  { date: "Oct 21, 2026", no: "JE-2026-0141", memo: "Auto-posted: Payment received for Invoice 26-12", amount: "$2,450.00" },
  { date: "Oct 20, 2026", no: "JE-2026-0140", memo: "Auto-posted: Invoice 26-14 for Northwind Studio", amount: "$1,260.00" },
];

const chart = [
  { group: "Assets", accounts: [
    { code: "1000", name: "Cash", balance: "$24,310.00" },
    { code: "1100", name: "Accounts Receivable", balance: "$12,100.00" },
    { code: "1200", name: "Undeposited Funds", balance: "$0.00" },
  ] },
  { group: "Liabilities", accounts: [
    { code: "2000", name: "Accounts Payable", balance: "$1,240.00" },
    { code: "2100", name: "Tax Payable", balance: "$310.00" },
  ] },
  { group: "Revenue", accounts: [
    { code: "4000", name: "Service Revenue", balance: "$17,200.00" },
    { code: "4100", name: "Product Sales", balance: "$1,200.00" },
  ] },
  { group: "Expenses", accounts: [
    { code: "6700", name: "Rent or Lease", balance: "$1,450.00" },
    { code: "6800", name: "Software & Subscriptions", balance: "$640.00" },
  ] },
];

const openReports = [
  { title: "AR Aging", copy: "View unpaid invoices by aging bucket and client." },
  { title: "Customer Profitability", copy: "Compare revenue, cost, and margin by customer." },
  { title: "Job Profitability", copy: "Track margin by project or job." },
];
const moreReports = [
  { title: "Profit & Loss", copy: "Revenue, expenses, and net income for a period." },
  { title: "Balance Sheet", copy: "Assets, liabilities, and equity at a point in time." },
  { title: "Cash Flow Statement", copy: "Cash inflows and outflows from operations, investing, and financing." },
];

const pnl = [
  { title: "Operating Revenue", total: "$18,400.00", prev: "$15,900.00", change: "+$2,500.00", up: true, lines: [
    { name: "Service Revenue", value: "$17,200.00", prev: "$14,900.00", change: "+$2,300.00" },
    { name: "Product Sales", value: "$1,200.00", prev: "$1,000.00", change: "+$200.00" },
  ] },
  { title: "Operating Expense", total: "$6,150.00", prev: "$6,480.00", change: "-$330.00", up: false, lines: [
    { name: "Wages & Salaries", value: "$3,650.00", prev: "$3,600.00", change: "+$50.00" },
    { name: "Rent or Lease", value: "$1,450.00", prev: "$1,500.00", change: "-$50.00" },
    { name: "Software & Subscriptions", value: "$640.00", prev: "$680.00", change: "-$40.00" },
    { name: "Travel & Meals", value: "$410.00", prev: "$700.00", change: "-$290.00" },
  ] },
];

const icons = {
  book: "M18 2H6c-1.1 0-2 .9-2 2v16c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 4h5v8l-2.5-1.5L6 12V4z",
  search: "M15.5 14h-.79l-.28-.27A6.47 6.47 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z",
};
</script>

<style scoped>
.acs-h { font-size: 13.5px; }
.acs-sub { margin: 2px 0 0; color: var(--saw-muted); font-size: 11.5px; line-height: 1.4; }
.acs-link { color: var(--saw-link); font-size: 11.5px; font-weight: 600; }

/* Wizard */
.saw-layer.acs-wiz { display: grid; grid-template-rows: auto minmax(0, 1fr) auto; }
.acs-steps { display: flex; justify-content: space-between; gap: 6px; margin: 0; padding: 0 0 4px; list-style: none; }
.acs-steps li { display: flex; flex: 1; align-items: center; gap: 6px; min-width: 0; color: var(--saw-muted); font-size: 11px; }
.acs-steps li:not(:last-child)::after { content: ""; flex: 1; height: 2px; border-radius: 1px; background: var(--saw-line); }
.acs-steps i { display: grid; flex: none; place-items: center; width: 22px; height: 22px; border: 1.5px solid var(--saw-line); border-radius: 50%; font-size: 10.5px; font-style: normal; font-weight: 700; }
.acs-steps li.is-on { color: var(--saw-text); font-weight: 600; }
.acs-steps li.is-on i { border-color: var(--saw-ink); background: var(--saw-ink); color: var(--saw-ink-text); }
.acs-steps li.is-done i { border-color: #2fae68; background: #2fae68; color: #fff; }
.acs-steps span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.acs-step { display: grid; grid-area: 2 / 1; align-content: start; gap: 8px; opacity: 0; visibility: hidden; transition: opacity 0.3s, visibility 0s linear 0.3s; }
.acs-step.is-on { opacity: 1; visibility: visible; transition: opacity 0.3s; }
.acs-tpl { display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px; }
.acs-tpl span { display: grid; gap: 2px; }
.acs-tpl b { font-size: 12.5px; }
.acs-tpl small { color: var(--saw-muted); font-size: 11px; line-height: 1.4; }
.acs-tpl.is-picked { border-color: var(--saw-ink); box-shadow: 0 0 0 1px var(--saw-ink); }
.acs-radio { flex: none; width: 14px; height: 14px; margin-top: 2px; border: 1.5px solid var(--saw-muted); border-radius: 50%; }
.is-picked .acs-radio { border: 4px solid var(--saw-ink); }
.acs-review { display: grid; margin: 0; padding: 4px 12px; }
.acs-review div { display: flex; justify-content: space-between; gap: 10px; border-top: 1px solid var(--saw-line); padding: 9px 0; font-size: 12px; }
.acs-review div:first-child { border-top: 0; }
.acs-review dt { color: var(--saw-muted); }
.acs-review dd { margin: 0; font-weight: 600; }
.acs-bar { display: flex; justify-content: space-between; border-top: 1px solid var(--saw-line); padding-top: 10px; }

/* Hub */
.acs-kpis { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 6px; margin: 0; }
.acs-kpis > div { min-width: 0; border: 1px solid var(--saw-line); border-radius: 8px; background: var(--saw-card); padding: 7px 10px; }
.acs-kpis dt { overflow: hidden; color: var(--saw-muted); font-size: 11px; text-overflow: ellipsis; white-space: nowrap; }
.acs-kpis dd { margin: 0; font-size: 15px; font-weight: 600; text-align: left; }
.acs-kpis small { display: block; color: var(--saw-muted); font-size: 10px; line-height: 1.35; }
.acs-je { overflow: hidden; }
.acs-je-head { display: flex; justify-content: space-between; align-items: center; background: var(--saw-wash); padding: 7px 12px; font-size: 12px; }
.acs-je-row { display: grid; grid-template-columns: minmax(0, 1fr) auto 5.4em; align-items: center; gap: 8px; min-height: 44px; border-top: 1px solid var(--saw-line); padding: 3px 12px; }
.acs-je-main { display: grid; min-width: 0; line-height: 1.3; }
.acs-je-main b { display: -webkit-box; overflow: hidden; font-size: 11.5px; font-weight: 600; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.acs-je-main small { color: var(--saw-muted); font-size: 10.5px; }
.acs-je-amt { font-size: 12px; text-align: right; }
.saw-layer.is-on .acs-je-row { animation: acs-in 0.45s cubic-bezier(0.33, 1, 0.68, 1) both; animation-delay: calc(var(--i) * 90ms + 150ms); }
@keyframes acs-in { from { opacity: 0; transform: translateY(-5px); } to { opacity: 1; transform: none; } }

/* Chart of accounts */
.acs-search { display: flex; align-items: center; gap: 6px; border: 1px solid var(--saw-line); border-radius: 6px; background: var(--saw-card); color: var(--saw-muted); padding: 6px 9px; font-size: 12px; }
.acs-search svg { width: 14px; height: 14px; fill: currentColor; }
.acs-tree { overflow: hidden; }
.acs-group { margin: 0; background: var(--saw-wash); padding: 5px 12px; font-size: 11.5px; }
.acs-acc { display: grid; grid-template-columns: 3em minmax(0, 1fr) auto; gap: 8px; border-top: 1px solid var(--saw-line); padding: 6px 12px 6px 22px; font-size: 12px; }
.acs-code { color: var(--saw-muted); }
.acs-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

/* Reports */
.acs-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 6px; }
.acs-rep { display: grid; align-content: start; gap: 3px; padding: 9px 10px; }
.acs-rep b { font-size: 12px; }
.acs-rep small { color: var(--saw-muted); font-size: 10.5px; line-height: 1.35; }
.acs-grid--dim { opacity: 0.5; }
.acs-more { margin: 4px 0 0; color: var(--saw-muted); font-size: 11px; font-weight: 600; }

/* Profit & Loss */
.acs-pnl-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; }
.acs-pnl-head > span:first-child { display: grid; }
.acs-pnl-head small { font-size: 11px; }
.acs-pl { overflow: hidden; padding: 4px 0; }
.acs-pl-row { display: grid; grid-template-columns: minmax(0, 1.6fr) repeat(3, minmax(0, 1fr)); gap: 8px; padding: 5px 12px; font-size: 11.5px; }
.acs-pl-row > :not(:first-child) { text-align: right; }
.acs-pl-cols { color: var(--saw-muted); font-size: 10.5px; }
.acs-pl-sec { border-top: 1px solid var(--saw-line); padding-top: 8px; }
.acs-pl-line { color: var(--saw-muted); padding-left: 22px; }
.acs-pl-line > span:first-child { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.acs-pl-net { margin: 6px 8px 2px; border-radius: 8px; background: var(--saw-green-fill); padding: 9px 10px; font-size: 12.5px; }
.acs-pl-net > b:nth-child(2) { color: var(--saw-green); }
.is-up { color: var(--saw-green); }
.is-down { color: var(--saw-red); }

@media (max-width: 639px) {
  .acs-steps span { display: none; }
  .acs-grid { grid-template-columns: minmax(0, 1fr); }
  .acs-rep small { display: none; }
  .acs-pl-row { grid-template-columns: minmax(0, 1fr) auto; }
  .acs-pl-row > :nth-child(n + 3) { display: none; }
  .acs-je-row em { display: none; }
  .acs-je-row { grid-template-columns: minmax(0, 1fr) 5.4em; }
  /* Narrow tiles and rows wrap instead of cutting labels and memos off. */
  .acs-kpis dt { white-space: normal; line-height: 1.25; }
  .acs-kpis small { display: none; }
  .acs-je-main b { -webkit-line-clamp: 3; }
  .acs-je-no { display: none; }
}
@media (prefers-reduced-motion: reduce) {
  .saw-layer.is-on .acs-je-row { animation: none; }
  .acs-step { transition: none; }
}
</style>
